import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const MarketsScreen = () => {
    const [markets, setMarkets] = useState([
        {
            symbol: 'BTC',
            name: 'Bitcoin',
            basePrice: 52340,
            price: 52340,
            change: 0,
            changePositive: true,
            volatility: 2.5,
            icon: '₿'
        },
        {
            symbol: 'ETH',
            name: 'Ethereum',
            basePrice: 2850,
            price: 2850,
            change: 0,
            changePositive: true,
            volatility: 3.2,
            icon: 'Ξ'
        },
        {
            symbol: 'ADA',
            name: 'Cardano',
            basePrice: 0.52,
            price: 0.52,
            change: 0,
            changePositive: false,
            volatility: 4.1,
            icon: '₳'
        },
        {
            symbol: 'DOT',
            name: 'Polkadot',
            basePrice: 7.25,
            price: 7.25,
            change: 0,
            changePositive: true,
            volatility: 3.8,
            icon: '●'
        },
        {
            symbol: 'SOL',
            name: 'Solana',
            basePrice: 108.50,
            price: 108.50,
            change: 0,
            changePositive: false,
            volatility: 5.2,
            icon: '◎'
        },
        {
            symbol: 'BNB',
            name: 'Binance Coin',
            basePrice: 352.18,
            price: 352.18,
            change: 0,
            changePositive: true,
            volatility: 3.5,
            icon: '⎈'
        },
        {
            symbol: 'XRP',
            name: 'Ripple',
            basePrice: 0.58,
            price: 0.58,
            change: 0,
            changePositive: false,
            volatility: 4.3,
            icon: '✕'
        },
        {
            symbol: 'DOGE',
            name: 'Dogecoin',
            basePrice: 0.12,
            price: 0.12,
            change: 0,
            changePositive: true,
            volatility: 6.1,
            icon: 'Ð'
        }
    ]);

    // Function to generate random price movement
    const generatePriceChange = (basePrice, volatility) => {
        const maxChange = volatility / 100;
        const randomChange = (Math.random() - 0.5) * 2 * maxChange;
        const newPrice = basePrice * (1 + randomChange);

        const changePercent = ((newPrice - basePrice) / basePrice) * 100;

        return {
            price: newPrice,
            change: changePercent,
            changePositive: changePercent >= 0
        };
    };

    // Update prices every 2 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setMarkets(prevMarkets =>
                prevMarkets.map(market => {
                    const newData = generatePriceChange(market.basePrice, market.volatility);

                    return {
                        ...market,
                        price: newData.price,
                        change: newData.change,
                        changePositive: newData.changePositive
                    };
                })
            );
        }, 2000); // Changed from 1000 to 2000

        return () => clearInterval(interval);
    }, []);

    // Format price based on its value
    const formatPrice = (price) => {
        if (price > 1000) {
            return `$${price.toFixed(0)}`;
        } else if (price > 1) {
            return `$${price.toFixed(2)}`;
        } else {
            return `$${price.toFixed(4)}`;
        }
    };

    // Format percentage change
    const formatChange = (change) => {
        const sign = change >= 0 ? '+' : '';
        return `${sign}${change.toFixed(2)}%`;
    };

    // Odometer-like animation for percentage changes with doubled duration
    const PercentageChange = ({ change, isPositive }) => {
        const formattedChange = formatChange(change);

        return (
            <div className="position-relative" style={{ height: '20px', overflow: 'hidden' }}>
                <motion.div
                    key={formattedChange}
                    initial={{ y: isPositive ? -20 : 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: isPositive ? 20 : -20, opacity: 0 }}
                    transition={{
                        type: "spring",
                        stiffness: 200, // Reduced stiffness for slower animation
                        damping: 15, // Adjusted damping for smoother motion
                        duration: 0.6 // Doubled from 0.3 to 0.6
                    }}
                    className={`position-absolute w-100 ${isPositive ? 'text-success' : 'text-danger'}`}
                    style={{ lineHeight: '20px' }}
                >
                    {formattedChange}
                </motion.div>
            </div>
        );
    };

    // Card hover animation variants
    const cardVariants = {
        initial: {
            scale: 1,
            y: 0,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        },
        hover: {
            scale: 1.02,
            y: -2,
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 17
            }
        },
        tap: {
            scale: 0.98,
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 17
            }
        }
    };

    // Icon container animation variants
    const iconVariants = {
        initial: { rotate: 0 },
        hover: {
            rotate: 360,
            transition: {
                duration: 0.6,
                ease: "easeInOut"
            }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="h-100"
        >
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold mb-0">Crypto Markets</h4>
                <div className="d-flex align-items-center">
                    <div className="d-flex align-items-center">
                        <motion.div
                            className="rounded-circle bg-danger me-1"
                            style={{ width: '8px', height: '8px' }}
                            animate={{
                                scale: [1, 1.5, 1],
                                opacity: [1, 0.5, 1],
                            }}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        />
                        <span className="text-danger small fw-bold">LIVE</span>
                    </div>
                </div>
            </div>

            <motion.div
                className="row g-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
            >
                {markets.map((market, index) => (
                    <div key={market.symbol} className="col-12">
                        <motion.div
                            className="card border-0 shadow-sm"
                            variants={cardVariants}
                            initial="initial"
                            whileHover="hover"
                            whileTap="tap"
                            animate="initial"
                            custom={index}
                        >
                            <div className="card-body">
                                <div className="row align-items-center">
                                    <div className="col-2">
                                        <motion.div
                                            className="rounded-circle d-flex align-items-center justify-content-center bg-warning"
                                            style={{ width: '45px', height: '45px' }}
                                            variants={iconVariants}
                                            whileHover="hover"
                                        >
                                            <span className="text-dark fw-bold" style={{ fontSize: '18px' }}>
                                                {market.icon}
                                            </span>
                                        </motion.div>
                                    </div>
                                    <div className="col-6">
                                        <h6 className="fw-bold mb-0">{market.symbol}</h6>
                                        <small className="text-muted">{market.name}</small>
                                    </div>
                                    <div className="col-4 text-end">
                                        <motion.h6
                                            key={market.price}
                                            className="fw-bold mb-0"
                                            initial={{ scale: 1 }}
                                            animate={{ scale: [1, 1.05, 1] }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            {formatPrice(market.price)}
                                        </motion.h6>
                                        <PercentageChange
                                            change={market.change}
                                            isPositive={market.changePositive}
                                        />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default MarketsScreen;